/**
 * Speed limits along a route.
 *
 * Mapbox returns a limit per geometry segment when asked for the `maxspeed`
 * annotation, and coverage in the UK is close to complete. Because that comes
 * back while *planning*, the limits can be stored with the route and read at
 * drive time with no network and no extra quota — the same reasoning that led
 * to storing the snapped geometry.
 *
 * Stored run-length encoded. A road's limit changes rarely and in long stretches
 * — a 24-mile route came back as roughly 1,200 segments across five distinct
 * values — so runs turn an array of a thousand numbers into a few dozen pairs.
 *
 * Limits are held in km/h regardless of where the route is. Mapbox reports
 * whichever unit the country signs in, and normalising on the way in means
 * display can be a presentation choice rather than something baked into storage.
 */

/** [number of segments, limit in km/h or null when unknown] */
export type SpeedRun = [number, number | null];

/** What Mapbox gives us per segment. */
export interface MaxspeedAnnotation {
  speed?: number;
  unit?: 'km/h' | 'mph';
  unknown?: boolean;
  none?: boolean;
}

const MPH_PER_KPH = 0.621371;

/** Normalises one annotation entry to km/h, or null if there's no usable limit. */
export function annotationToKph(entry: MaxspeedAnnotation | null | undefined): number | null {
  if (!entry || entry.unknown || entry.none) return null;
  if (typeof entry.speed !== 'number' || !Number.isFinite(entry.speed)) return null;
  return entry.unit === 'mph' ? entry.speed / MPH_PER_KPH : entry.speed;
}

export function encodeSpeedRuns(values: Array<number | null>): SpeedRun[] {
  const runs: SpeedRun[] = [];
  for (const value of values) {
    const last = runs[runs.length - 1];
    // Compared with a tolerance: a limit signed in mph and converted to km/h
    // lands on values like 48.28, and floating point makes exact equality
    // between two of those a coin toss.
    if (last && nearlyEqual(last[1], value)) last[0] += 1;
    else runs.push([1, value]);
  }
  return runs;
}

function nearlyEqual(a: number | null, b: number | null): boolean {
  if (a === null || b === null) return a === b;
  return Math.abs(a - b) < 0.5;
}

export function decodeSpeedRuns(runs: SpeedRun[]): Array<number | null> {
  const out: Array<number | null> = [];
  for (const [count, value] of runs) {
    for (let i = 0; i < count; i++) out.push(value);
  }
  return out;
}

/** The limit on a given geometry segment, without expanding the runs. */
export function speedLimitAt(runs: SpeedRun[] | undefined, segmentIndex: number): number | null {
  if (!runs || segmentIndex < 0) return null;
  let position = 0;
  for (const [count, value] of runs) {
    position += count;
    if (segmentIndex < position) return value;
  }
  return null;
}

export type SpeedUnit = 'mph' | 'kph';

/** What to print under the number. */
export function speedUnitLabel(unit: SpeedUnit): string {
  return unit === 'kph' ? 'km/h' : 'mph';
}

/**
 * The driver's own speed, rounded to whole units.
 *
 * Whole units because nothing below one is meaningful here: GPS speed wanders
 * by more than that at a standstill, and no one reads a decimal at a glance.
 */
export function formatSpeed(speedMps: number, unit: SpeedUnit = 'mph'): string {
  const converted = unit === 'kph' ? speedMps * 3.6 : speedMps * MPH_PER_KPH * 3.6;
  return String(Math.max(0, Math.round(converted)));
}

/**
 * Formats a limit for display.
 *
 * Rounded to the nearest sign-able value rather than to the nearest integer.
 * A 30 mph limit stored as 48.28 km/h converts back to 30.0, but a 40 stored as
 * 64.37 converts to 39.998, and "40" is the only answer a driver wants to see.
 */
export function formatSpeedLimit(kph: number | null, unit: SpeedUnit = 'mph'): string | null {
  if (kph === null) return null;
  if (unit === 'kph') return `${Math.round(kph / 5) * 5}`;

  const mph = kph * MPH_PER_KPH;
  // UK limits are all multiples of 10 except 20 and 5 mph zones.
  const rounded = mph < 25 ? Math.round(mph / 5) * 5 : Math.round(mph / 10) * 10;
  return `${rounded}`;
}

/** Whether the driver is over the limit, with a tolerance for GPS speed noise. */
export function isOverLimit(
  speedMps: number | null,
  limitKph: number | null,
  tolerancePercent = 10,
): boolean {
  if (speedMps === null || limitKph === null || limitKph <= 0) return false;
  const speedKph = speedMps * 3.6;
  return speedKph > limitKph * (1 + tolerancePercent / 100);
}
